import TimeSignature from '../uTheory.Notation/timeSignature.ts';
import Slider from './slider.ts';
import utils from '../utils.ts';
import sizeDiv from './sizeDiv.ts';

const upperPartValues = [1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 15];
const lowerPartValues = [1, 2, 4, 8, 16, 32];

/**
 */
export default class TimeSignatureSlider {
  /**
   * UI for adjusting the value of a time signature
   *
   * @param {HTMLDivElement} div where to insert the sliders.
   * @param {TimeSignature} timeSignature
   * @param {object} options
   * @param {boolean} [options.displayOnly] default true, don't change the underlying value of the TS,
   * if false, will update timeSignature only when a valid TS is made.
   * @param {Function} options.onChange callback with time signature string, like "14/4"
   *
   * @property {number} upper current upper value; can be set
   * @property {number} lower current lower value; can be set
   * @property {TimeSignature} timeSignature
   */
  constructor(div, timeSignature, options) {
    if (!(timeSignature instanceof TimeSignature))
      throw new Error('timeSignature must be a TimeSignature');
    const { sliders } = makeTemplate(this, div);
    Object.assign(this, { div, timeSignature, sliders, options });
  }

  change() {
    this.timeSignature.displayAs = this.value;
    if (this.options.onChange) this.options.onChange(this.value);
  }

  resize(height) {
    const measureHeightCallback = () => Math.max(this.div.scrollHeight, this.div.clientHeight);
    const measureWidthCallback = () => this.div.clientWidth;
    const setSizeCallback = (percent) => {
      this.div.style.width = '100%';
      // Redraw at ideal scale:
      this.sliders.upper.redraw();
      this.sliders.lower.redraw();
      // Scale the whole thing down as needed:
      this.div.style.width = `${percent}%`;
    };
    const range = [20, 100];
    const steps = 1;
    const options = {
      measureHeightCallback,
      measureWidthCallback,
      setSizeCallback,
      range,
      steps,
    };
    return sizeDiv(this.div.clientWidth, height, this.div, options);
  }

  /**
   * @param {string} value "4/4", etc...
   */
  set(value) {
    const parts = value.split('/').map(Number);
    this.upper = parts[0];
    this.lower = parts[1];
  }

  get upper() {
    return this.sliders.upper.value;
  }

  set upper(num) {
    if (typeof num !== 'number') throw new Error('Time sig upper must be a number.');
    this.sliders.upper.value = num;
    this.change();
  }

  get lower() {
    return this.sliders.lower.value;
  }

  set lower(num) {
    if (typeof num !== 'number') throw new Error('Time sig lower must be a number.');
    this.sliders.lower.value = num;
    this.change();
  }

  get value() {
    return `${this.upper}/${this.lower}`;
  }

  set disabled(bool) {
    this.sliders.upper.disabled = bool;
    this.sliders.lower.disabled = bool;
  }
}

function makeTemplate(context, div) {
  const id = `time-signature-sliders-${utils.uuid}`;
  const template = {
    id,
    template: `
      <div id = "${id}" class='time-signature-sliders'>
        <div class='time-signature-slider-group'>
          <label for='time-signature-slider-upper' class='time-signature-label'>Upper:</label>
          <div id='time-signature-slider-upper' class='time-signature-slider'></div>
        </div>
        <div class='time-signature-slider-group'>
          <label for='time-signature-slider-lower' class='time-signature-label'>Lower:</label>
          <div id='time-signature-slider-lower' class='time-signature-slider'></div>
        </div>
      </div>
      <style>
        .time-signature-slider-group {
          display: flex;
          flex-direction: row;
          flex-wrap: nowrap;
          align-items: center;
          /* margin: 2vw 2vw 0 2vw; */
        }
        .time-signature-label {
          flex-grow: 0;
          margin-right: 2vw;
          font-size: 4vmin;
        }
        .time-signature-slider {
          flex-grow: 1;
        }
        .time-signature-sliders {
          padding-bottom: 10px;
        }
      </style>
    `,
  };
  div.style.margin = 'auto';
  div.innerHTML = template.template;
  const groups = document.getElementById(id).querySelectorAll('.time-signature-slider-group');
  const upperDiv = groups[0].querySelector('.time-signature-slider');
  const lowerDiv = groups[1].querySelector('.time-signature-slider');
  template.sliders = makeSliders(context, upperDiv, lowerDiv);
  return template;
}

function makeSliders(context, upperDiv, lowerDiv) {
  return {
    upper: makeSlider(context, upperDiv, upperPartValues, 'upper'),
    lower: makeSlider(context, lowerDiv, lowerPartValues, 'lower'),
  };
}

function makeSlider(context, div, values, upperLower) {
  return new Slider(div, {
    range: [values[0], values[values.length - 1]],
    value: 4,
    steps: valuesToSteps(values),
    ticks: valuesToSteps(values),
    // ticks: [0, 3, 4, 4, 3],
    onChange: (val) => {
      context[upperLower] = val;
    },
  });
}

function valuesToSteps(values) {
  return values.map((value, i) => {
    if (!i) return 0;
    return value - values[i - 1];
  });
}
